import './settings/settings-leaf.css';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { billingApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { SESSION_QUERY_KEY, useSession } from '../state/session.tsx';
import { invalidateQuoteViews } from '../state/quote-cache.ts';
import { FounderContactLine } from './founder-contact.tsx';
import { Alert, Button, ConfirmDialog, LoadingBlock, classes } from './ui.tsx';
import { getDateLocale } from '../i18n/locale.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import {
  FREE_PLAN_ID,
  PAID_PLAN_IDS,
  RECOMMENDED_PLAN_ID,
  PLAN_IDS,
  YEARLY_PRICE_MULTIPLIER,
  isPaidPlanId,
  planHasFeature,
  planRanksBelow,
  type BillingInterval,
  type PaidPlanId,
  type PlanId,
} from '@shared/constants/index.ts';
import type { BillingState } from '@shared/types/index.ts';

/**
 * What Pro adds, in the order it tends to pay for itself: the walkthrough
 * that writes the quote, the texts customers actually answer, and the reports
 * that say which jobs are worth doing. Keys into `billing.proPitch.*`.
 */
const PRO_HIGHLIGHTS = ['magicQuote', 'texting', 'reports', 'api'] as const;

function PlanSelectionLabel({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--color-brand-strong)]">
      <span
        aria-hidden="true"
        className="flex size-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-brand)] text-xs text-white"
      >
        ✓
      </span>
      {label}
    </span>
  );
}

/**
 * The app subscription (Settings → Billing): the tier the company pays for to
 * use the app — separate from "Payments", which is the company's own gateway
 * for charging its cleaning customers.
 *
 * What lives here is the choice between tiers — picking one to subscribe to,
 * or moving a live subscription between them in place — plus the seat and
 * location lines that explain what the next invoice is made of.
 *
 * Nothing money-shaped is decided here: the buttons follow a URL the API
 * hands back to a Stripe-hosted page — Checkout to subscribe, the Customer
 * Portal for everything after.
 */
export default function BillingSection() {
  const { t } = useTranslation();
  const { user, company } = useSession();
  const queryClient = useQueryClient();
  const [redirecting, setRedirecting] = useState<'checkout' | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PaidPlanId | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval | null>(null);
  const [confirmingEndTrial, setConfirmingEndTrial] = useState(false);

  // Checkout's success redirect can beat the webhook back to the app, so
  // while ?billing=subscribed is in the URL the state is polled until the
  // subscription shows up — instead of showing a stale Subscribe button.
  // `past_due` counts as arrived too: an `incomplete` first payment maps to
  // it, access is granted, and no further status change may ever come.
  const checkoutResolved = (status: string | undefined) => status === 'active' || status === 'past_due';
  const returnedFromCheckout = new URLSearchParams(window.location.search).get('billing') === 'subscribed';
  const billing = useQuery({
    queryKey: ['billing'],
    queryFn: () => billingApi.get(),
    refetchInterval: (query) => (returnedFromCheckout && !checkoutResolved(query.state.data?.status) ? 3000 : false),
  });
  const isOwner = user?.role === 'owner';

  // Once the webhook lands, refresh the session too so the trial banner (or
  // lock screen) in the app shell clears without a reload.
  const subscriptionArrived = checkoutResolved(billing.data?.status);
  useEffect(() => {
    if (returnedFromCheckout && subscriptionArrived) {
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    }
  }, [returnedFromCheckout, subscriptionArrived, queryClient]);

  const changePlan = useMutation({
    mutationFn: ({ plan, interval }: { plan: PaidPlanId; interval?: BillingInterval }) =>
      billingApi.changePlan(plan, interval),
    onSuccess: async (next, variables) => {
      queryClient.setQueryData(['billing'], next);
      const chosen = next.plan ?? next.effectivePlan;
      setNotice(
        variables.interval && variables.plan === chosen
          ? t('billing.intervalChanged')
          : t('billing.planChanged', { name: next.plans[chosen].name }),
      );
      // effectivePlan gates features all over the app, so the session's
      // company record must follow the change.
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
    onError: (caught) => {
      setError(caught instanceof ApiRequestError ? caught.message : t('billing.changePlanError'));
    },
  });

  /**
   * Moving to Free. Not a plan change at the gateway — there is nothing there
   * to change — so it has its own mutation, and the same session refresh
   * behind it: `effectivePlan` decides what the rest of the app renders.
   */
  const chooseFree = useMutation({
    mutationFn: () => billingApi.chooseFree(),
    onSuccess: async (next) => {
      queryClient.setQueryData(['billing'], next);
      setNotice(t('billing.freeChosen'));
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      // Free ends every live series and recounts quote slots before this
      // returns. The session refresh does not refresh quotes, so an accepted
      // quote would keep reading Scheduled until the page was reloaded.
      invalidateQuoteViews(queryClient);
    },
    onError: (caught) => {
      setError(caught instanceof ApiRequestError ? caught.message : t('billing.changePlanError'));
    },
  });

  async function redirect(kind: 'checkout' | 'portal', plan?: PaidPlanId, interval?: BillingInterval) {
    setRedirecting(kind);
    setError(null);
    try {
      const { url } =
        kind === 'checkout' && plan !== undefined
          ? await billingApi.checkout(plan, interval ?? 'month')
          : await billingApi.portal();
      window.location.assign(url);
    } catch (caught) {
      setRedirecting(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('billing.genericError'));
    }
  }

  if (billing.isLoading) {
    return (
      <section className="card work-section settings-leaf mb-6" aria-labelledby="billing-heading">
        <div className="work-section-heading">
          <h2 id="billing-heading" className="mb-3 text-lg font-semibold">
            {t('billing.heading')}
          </h2>
        </div>
        <LoadingBlock label={t('common.loading')} />
      </section>
    );
  }

  const state = billing.data;
  if (!state) return null;

  // A canceled subscription keeps its id — the Portal still serves its
  // invoices — so `subscribed` stays true after cancellation and cannot
  // decide what the controls offer. Only a LIVE subscription can be moved
  // between plans; a canceled company needs the way back in, which is a
  // fresh Checkout.
  const hasLiveSubscription = state.status === 'active' || state.status === 'past_due';

  // Formatted in the deployment's billing currency rather than assembled from
  // a bare number and a '$' in the translation: the plans are whatever the
  // Stripe prices say they are, and they are not always dollars.
  const planMoney = (cents: number) => formatCents(cents, state.currencyCode, getDateLocale());

  // The tier whose price a downgrade must be judged from: the chosen plan,
  // or Business while a trialing company enjoys everything.
  const currentPlanId = state.plan ?? state.effectivePlan;
  // What the Subscribe button would buy. Free is a choice but never a
  // purchase, so a company sitting on it still has Pro pre-selected here.
  const pickedPlan: PaidPlanId = selectedPlan ?? (isPaidPlanId(state.plan) ? state.plan : 'pro');
  // A company that signed up on Free still starts with a live trial. Until it
  // ends, Free is not its current plan yet: it keeps the Switch to Free button
  // so the owner can end the trial early and drop to Free.
  const onFreePlan = state.plan === FREE_PLAN_ID && !(Boolean(state.trialEndsAt) && trialStillRunning(state));
  // Whether there is anything to recommend: a company already on Pro or
  // Business is not sold Pro, and nor is one whose trial has not started.
  const belowRecommended = planRanksBelow(state.effectivePlan, RECOMMENDED_PLAN_ID);
  // Which cycle the plan cards quote. Only meaningful pre-subscription (it
  // picks what Checkout sells); a live subscription switches through the
  // explicit buttons below instead.
  const pickedInterval: BillingInterval = state.yearlyAvailable ? (selectedInterval ?? state.interval) : 'month';
  const showsLocations = state.locations > 1 || planHasFeature(state.effectivePlan, 'multi_location');
  const freeCardClasses = classes(
    'rounded-lg border p-4 text-left',
    onFreePlan
      ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
      : 'border-[color:var(--color-line)]',
  );

  const yearlyMoney = (monthlyCents: number) => planMoney(monthlyCents * YEARLY_PRICE_MULTIPLIER);

  function requestIntervalSwitch(target: BillingInterval) {
    if (!state || !isPaidPlanId(currentPlanId)) return;
    setError(null);
    setNotice(null);
    const confirmed = window.confirm(
      target === 'year' ? t('billing.switchToYearlyConfirm') : t('billing.switchToMonthlyConfirm'),
    );
    if (!confirmed) return;
    changePlan.mutate({ plan: currentPlanId, interval: target });
  }

  /**
   * Confirms before dropping to Free: it is the one move in this panel that
   * takes features away without a gateway page in between to think on.
   *
   * A trial that is still running ends the moment Free is chosen, so that
   * confirmation says so and names the two answers. Every other company
   * keeps the browser confirm that describes what Free keeps and what it
   * stops.
   */
  function requestFreePlan() {
    if (!state) return;
    setError(null);
    setNotice(null);
    if (trialStillRunning(state)) {
      setConfirmingEndTrial(true);
      return;
    }
    if (!window.confirm(t('billing.switchToFreeConfirm'))) return;
    chooseFree.mutate();
  }

  /**
   * Every switch on a live subscription moves the price at once (Stripe
   * prorates it), so the new plan and its price go in front of the owner
   * before it applies — one tap on the wrong card is otherwise a new bill.
   * Downgrades off multi_location also archive (lock — never delete) every
   * active location beyond the primary one, so that same question carries
   * the exact count.
   */
  async function requestPlanChange(target: PaidPlanId) {
    if (!state) return;
    setError(null);
    setNotice(null);
    const definition = state.plans[target];
    let question = t('billing.switchPlanConfirm', {
      plan: definition.name,
      price:
        state.interval === 'year'
          ? t('billing.perYear', { amount: yearlyMoney(definition.basePriceCents) })
          : t('billing.perMonth', { amount: planMoney(definition.basePriceCents) }),
    });
    if (planHasFeature(currentPlanId, 'multi_location') && !planHasFeature(target, 'multi_location')) {
      let lockedLocations = 0;
      try {
        ({ lockedLocations } = await billingApi.downgradeImpact(target));
      } catch (caught) {
        setError(caught instanceof ApiRequestError ? caught.message : t('billing.changePlanError'));
        return;
      }
      if (lockedLocations > 0) {
        question += `\n\n${t('billing.downgradeLockConfirm', { count: lockedLocations, plan: definition.name })}`;
      }
    }
    if (!window.confirm(question)) return;
    changePlan.mutate({ plan: target });
  }

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="billing-heading">
      <div className="work-section-heading">
        <h2 id="billing-heading" className="mb-3 text-lg font-semibold">
          {t('billing.heading')}
        </h2>
      </div>

      {state.demoMode ? (
        /* Nothing to sell here, so this panel explains the demo instead of
           offering a plan: how long an account lasts, what happens when it
           runs out, and that the data can always be exported. */
        <>
          <Alert
            tone={state.locked ? 'error' : 'warning'}
            title={state.locked ? t('billing.demoLockedTitle') : t('billing.demoTitle', { count: state.trialDaysLeft })}
          >
            <p>{t('billing.demoMessage', { count: state.demoExpirationDays })}</p>
            <p className="mt-2">{t('billing.demoNotForProduction')}</p>
          </Alert>
          <p className="mt-4 text-sm text-[color:var(--color-muted)]">{t('billing.demoExtendNote')}</p>
        </>
      ) : (
        <>
          {/* A live trial only: a Free company keeps `trialing` after its
              trial runs out, and "0 days left" is not a trial. */}
          {trialStillRunning(state) ? (
            <Alert tone="info" title={t('billing.trialTitle', { count: state.trialDaysLeft })}>
              <p>{t('billing.trialMessage')}</p>
              {isOwner ? <FounderContactLine className="mt-2 text-sm" /> : null}
            </Alert>
          ) : null}

          {state.locked ? (
            <Alert tone="error" title={t('billing.lockedTitle')}>
              <p>{isOwner ? t('billing.lockedMessageOwner') : t('billing.lockedMessageNonOwner')}</p>
              {/* `locked` is also a Free account paused for a year. That is
                  not a lapsed trial, and the number is not for it. */}
              {isOwner && company?.accessLockedReason !== 'dormant' ? (
                <FounderContactLine className="mt-2 text-sm" />
              ) : null}
            </Alert>
          ) : null}

          {state.status === 'past_due' ? (
            <Alert tone="warning" title={t('billing.pastDueTitle')}>
              <p>{t('billing.pastDueMessage')}</p>
            </Alert>
          ) : null}

          {state.cancelAtPeriodEnd ? (
            <Alert tone="warning" title={t('billing.cancelScheduledTitle')}>
              <p>
                {state.billingPeriodEnd
                  ? t('billing.cancelScheduledOn', {
                      date: new Intl.DateTimeFormat(getDateLocale()).format(new Date(state.billingPeriodEnd)),
                    })
                  : t('billing.cancelScheduled')}
              </p>
            </Alert>
          ) : null}

          {state.status === 'active' ? (
            <p className="text-sm">
              <span className="font-semibold text-[color:var(--color-brand)]">{t('billing.activeMessage')}</span>{' '}
              {state.billingPeriodEnd
                ? t('billing.periodEnds', {
                    date: new Intl.DateTimeFormat(getDateLocale()).format(new Date(state.billingPeriodEnd)),
                  })
                : ''}
            </p>
          ) : null}

          {hasLiveSubscription ? (
            <p className="mt-1 text-sm text-[color:var(--color-muted)]">
              {t('billing.cycleLabel')}:{' '}
              {state.interval === 'year' ? t('billing.cycleYearly') : t('billing.cycleMonthly')}
            </p>
          ) : null}

          <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <div className="flex justify-between gap-4 sm:block">
              <dt className="font-medium text-[color:var(--color-muted)]">{t('billing.planLabel')}</dt>
              <dd>
                {onFreePlan
                  ? t('billing.freePlanLine')
                  : state.plan
                    ? t('billing.planLine', {
                        name: state.plans[state.plan].name,
                        base: planMoney(state.plans[state.plan].basePriceCents),
                      })
                    : state.status === 'trialing'
                      ? t('billing.trialPlanLine')
                      : t('billing.noPlanLine')}
              </dd>
            </div>
            <div className="flex justify-between gap-4 sm:block">
              <dt className="font-medium text-[color:var(--color-muted)]">{t('billing.activeUsersLabel')}</dt>
              <dd>
                {t('billing.activeUsersCount', { count: state.seats })}
                {t('billing.seatsIncludedNote', {
                  included: state.includedSeats,
                  extra: planMoney(state.extraSeatPriceCents),
                })}
                {hasLiveSubscription && state.seats > state.includedSeats
                  ? t('billing.extraSeatsNote', {
                      amount: planMoney((state.seats - state.includedSeats) * state.extraSeatPriceCents),
                    })
                  : ''}
              </dd>
            </div>
            {showsLocations ? (
              <div className="flex justify-between gap-4 sm:block">
                <dt className="font-medium text-[color:var(--color-muted)]">{t('billing.locationsLabel')}</dt>
                <dd>
                  {t('billing.locationsCount', { count: state.locations })}
                  {state.extraLocationPriceCents !== null
                    ? t('billing.locationsIncludedNote', {
                        included: state.includedLocations,
                        extra: planMoney(state.extraLocationPriceCents),
                      })
                    : t('billing.locationsNeedBusinessNote')}
                  {hasLiveSubscription &&
                  state.extraLocationPriceCents !== null &&
                  state.locations > state.includedLocations
                    ? t('billing.extraLocationsNote', {
                        amount: planMoney((state.locations - state.includedLocations) * state.extraLocationPriceCents),
                      })
                    : ''}
                </dd>
              </div>
            ) : null}
            {state.discountLabel ? (
              <div className="flex justify-between gap-4 sm:block">
                <dt className="font-medium text-[color:var(--color-muted)]">{t('billing.discountHeading')}</dt>
                <dd>{state.discountLabel}</dd>
              </div>
            ) : null}
            {state.pendingPromoCode ? (
              <div className="flex justify-between gap-4 sm:block">
                <dt className="font-medium text-[color:var(--color-muted)]">{t('billing.discountHeading')}</dt>
                <dd>{t('billing.pendingPromoNote', { code: state.pendingPromoCode })}</dd>
              </div>
            ) : null}
          </dl>

          {notice ? (
            <div className="mt-4">
              <Alert tone="success">{notice}</Alert>
            </div>
          ) : null}

          {error ? (
            <div className="mt-4">
              <Alert tone="error" title={t('billing.openErrorTitle')}>
                <p>{error}</p>
              </Alert>
            </div>
          ) : null}

          {isOwner ? (
            <>
              <h3 className="mt-6 mb-1 text-base font-semibold">
                {hasLiveSubscription ? t('billing.changePlanHeading') : t('billing.choosePlanHeading')}
              </h3>
              <p className="mb-3 text-sm text-[color:var(--color-muted)]">
                {hasLiveSubscription ? t('billing.changePlanDescription') : t('billing.choosePlanDescription')}
              </p>

              {/* Stripe Tax computes the amount, so there is no rate to
                  quote — but a live subscriber's plan switch bills the taxed
                  total straight from here, with no hosted page in between
                  to itemize it first. Said in words, before the click. */}
              {state.salesTax.status === 'gateway' ? (
                <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('billing.salesTaxGatewayNote')}</p>
              ) : null}

              {!hasLiveSubscription && state.yearlyAvailable ? (
                <div
                  className="mb-3 inline-flex max-w-full flex-wrap rounded-lg border border-[color:var(--color-line)] p-1"
                  role="radiogroup"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={pickedInterval === 'month'}
                    onClick={() => setSelectedInterval('month')}
                    className={classes(
                      'rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap',
                      pickedInterval === 'month'
                        ? 'bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                        : 'text-[color:var(--color-muted)]',
                    )}
                  >
                    {t('billing.intervalMonthly')}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={pickedInterval === 'year'}
                    onClick={() => setSelectedInterval('year')}
                    className={classes(
                      'rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap',
                      pickedInterval === 'year'
                        ? 'bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                        : 'text-[color:var(--color-muted)]',
                    )}
                  >
                    {t('billing.intervalYearly')}
                    <span className="ml-1.5 badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">
                      {t('billing.yearlySave')}
                    </span>
                  </button>
                </div>
              ) : null}

              {/* The Free card sits with the paid ones because it is a real
                  choice between the same four tiers, but it is not a purchase:
                  it never joins the radio group Checkout reads, and it carries
                  its own button. */}
              {/* The case for Pro, made once and only to companies that are
                  not on it: what the tier adds, in the order it tends to pay
                  for itself. A trialing company sees it too — it is enjoying
                  these features right now and is about to choose whether to
                  keep them. */}
              {belowRecommended ? (
                <div className="mb-4 rounded-lg border border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] p-4">
                  <p className="font-semibold text-[color:var(--color-brand-strong)]">
                    {t('billing.proPitch.heading', { plan: state.plans[RECOMMENDED_PLAN_ID].name })}
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-[color:var(--color-brand-strong)]">
                    {PRO_HIGHLIGHTS.map((highlight) => (
                      <li key={highlight}>· {t(`billing.proPitch.${highlight}`)}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-sm text-[color:var(--color-brand-strong)]">
                    {t('billing.proPitch.price', {
                      amount: planMoney(state.plans[RECOMMENDED_PLAN_ID].basePriceCents),
                      count: state.plans[RECOMMENDED_PLAN_ID].includedSeats,
                    })}
                  </p>
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className={freeCardClasses}>
                  <div className="flex h-full flex-col">
                    <p className="font-semibold">{state.plans[FREE_PLAN_ID].name}</p>
                    <div className="mt-2 min-h-6">
                      {onFreePlan ? <PlanSelectionLabel label={t('billing.currentPlanBadge')} /> : null}
                    </div>
                    <p className="mt-1 text-xl font-bold">{t('billing.freePrice')}</p>
                    <p className="mt-1 text-sm text-[color:var(--color-muted)]">
                      {t('billing.freeSeats', { count: state.plans[FREE_PLAN_ID].includedSeats })}
                    </p>
                    <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('billing.planBlurb.free')}</p>
                    <div className="mt-auto pt-3">
                      {onFreePlan ? (
                        <p className="text-sm text-[color:var(--color-muted)]">{t('billing.freeCurrentNote')}</p>
                      ) : hasLiveSubscription ? (
                        // Cancellation lives in the Customer Portal, so this
                        // says where rather than offering a second way out.
                        <p className="text-sm text-[color:var(--color-muted)]">{t('billing.freeCancelFirstNote')}</p>
                      ) : (
                        <Button busy={chooseFree.isPending} disabled={chooseFree.isPending} onClick={requestFreePlan}>
                          {t('billing.switchToFree')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="contents"
                  role={hasLiveSubscription ? undefined : 'radiogroup'}
                  aria-label={hasLiveSubscription ? undefined : t('billing.choosePlanHeading')}
                >
                  {PAID_PLAN_IDS.map((planId) => {
                    const definition = state.plans[planId];
                    const isCurrent = hasLiveSubscription && state.plan === planId;
                    const isPicked = !hasLiveSubscription && pickedPlan === planId;
                    const businessLocationPrice = state.plans.business.extraLocationPriceCents;
                    // Every price on the card follows the picked cycle — quoting
                    // a yearly base over monthly seat add-ons would misstate the
                    // invoice Checkout is about to present.
                    const quotesYearly = !hasLiveSubscription && pickedInterval === 'year';
                    // Keep the recommendation label when Pro is selected/current;
                    // the green selection surface takes priority over its blue fill.
                    const isRecommended = planId === RECOMMENDED_PLAN_ID;
                    const card = (
                      <div className="flex h-full flex-col">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="font-semibold">{definition.name}</p>
                          {isRecommended ? (
                            <span className="badge border-[color:var(--color-line)] bg-[color:var(--color-surface)] text-[color:var(--color-muted)]">
                              {t('billing.recommendedBadge')}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-2 min-h-6">
                          {isCurrent || isPicked ? (
                            <PlanSelectionLabel
                              label={t(isCurrent ? 'billing.currentPlanBadge' : 'billing.selectedPlanBadge')}
                            />
                          ) : null}
                        </div>
                        <p className="mt-1 text-xl font-bold">
                          {quotesYearly
                            ? t('billing.perYear', { amount: yearlyMoney(definition.basePriceCents) })
                            : t('billing.perMonth', { amount: planMoney(definition.basePriceCents) })}
                        </p>
                        <p className="mt-1 text-sm text-[color:var(--color-muted)]">
                          {quotesYearly
                            ? t('billing.planSeatsYear', {
                                count: definition.includedSeats,
                                extra: yearlyMoney(definition.extraSeatPriceCents),
                              })
                            : t('billing.planSeats', {
                                count: definition.includedSeats,
                                extra: planMoney(definition.extraSeatPriceCents),
                              })}
                        </p>
                        <p className="mt-2 text-sm text-[color:var(--color-muted)]">
                          {planId === 'business'
                            ? t(quotesYearly ? 'billing.planBlurb.businessYear' : 'billing.planBlurb.business', {
                                extraLocation: quotesYearly
                                  ? yearlyMoney(businessLocationPrice ?? 4500)
                                  : planMoney(businessLocationPrice ?? 4500),
                              })
                            : t(`billing.planBlurb.${planId}`)}
                        </p>
                        {hasLiveSubscription && !isCurrent ? (
                          <div className="mt-auto pt-3">
                            <Button
                              busy={changePlan.isPending && changePlan.variables?.plan === planId}
                              disabled={changePlan.isPending}
                              onClick={() => void requestPlanChange(planId)}
                            >
                              {t('billing.switchTo', { name: definition.name })}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    );
                    const cardClasses = classes(
                      'rounded-lg border p-4 text-left',
                      isCurrent || isPicked
                        ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                        : isRecommended
                          ? 'border-[color:var(--color-plan-recommended)] bg-[color:var(--color-plan-recommended-soft)]'
                          : 'border-[color:var(--color-line)]',
                    );
                    // Unsubscribed: the whole card is the radio choosing the
                    // Checkout plan. Subscribed: it is a plain panel whose
                    // "Switch to" button does the work.
                    return hasLiveSubscription ? (
                      <div key={planId} className={cardClasses}>
                        {card}
                      </div>
                    ) : (
                      <button
                        key={planId}
                        type="button"
                        role="radio"
                        aria-checked={isPicked}
                        tabIndex={isPicked ? 0 : -1}
                        onKeyDown={(event) => {
                          const direction =
                            event.key === 'ArrowRight' || event.key === 'ArrowDown'
                              ? 1
                              : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                                ? -1
                                : 0;
                          if (!direction) return;
                          event.preventDefault();
                          const index =
                            (PAID_PLAN_IDS.indexOf(planId) + direction + PAID_PLAN_IDS.length) % PAID_PLAN_IDS.length;
                          const nextPlan = PAID_PLAN_IDS[index];
                          if (!nextPlan) return;
                          setSelectedPlan(nextPlan);
                          event.currentTarget.parentElement
                            ?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
                            [index]?.focus();
                        }}
                        onClick={() => setSelectedPlan(planId)}
                        className={cardClasses}
                      >
                        {card}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                {hasLiveSubscription ? (
                  <>
                    <Button variant="primary" busy={redirecting === 'portal'} onClick={() => void redirect('portal')}>
                      {t('billing.manageBilling')}
                    </Button>
                    {state.yearlyAvailable ? (
                      <Button
                        busy={changePlan.isPending && changePlan.variables?.interval !== undefined}
                        disabled={changePlan.isPending}
                        onClick={() => requestIntervalSwitch(state.interval === 'year' ? 'month' : 'year')}
                      >
                        {state.interval === 'year' ? t('billing.switchToMonthly') : t('billing.switchToYearly')}
                        {state.interval !== 'year' ? (
                          <span className="ml-1.5 badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">
                            {t('billing.yearlySave')}
                          </span>
                        ) : null}
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      busy={redirecting === 'checkout'}
                      onClick={() => void redirect('checkout', pickedPlan, pickedInterval)}
                    >
                      {t('billing.startSubscription', { name: state.plans[pickedPlan].name })}
                    </Button>
                    {/* A canceled company still has invoices in the Portal. */}
                    {state.portalAvailable ? (
                      <Button busy={redirecting === 'portal'} onClick={() => void redirect('portal')}>
                        {t('billing.billingHistory')}
                      </Button>
                    ) : null}
                  </>
                )}
              </div>
            </>
          ) : (
            <p className="mt-4 text-sm text-[color:var(--color-muted)]">{t('billing.ownerOnly')}</p>
          )}
        </>
      )}
      {confirmingEndTrial ? (
        <ConfirmDialog
          title={t('billing.endTrialForFreeTitle')}
          confirmLabel={t('billing.switchToFree')}
          cancelLabel={t('billing.keepTrial')}
          confirmVariant="danger"
          busy={chooseFree.isPending}
          onCancel={() => {
            if (!chooseFree.isPending) setConfirmingEndTrial(false);
          }}
          onConfirm={() => {
            setConfirmingEndTrial(false);
            chooseFree.mutate();
          }}
        >
          <p>{t('billing.endTrialForFreeBody')}</p>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

/**
 * Whether Free would end a trial that is still granting paid features.
 *
 * Matches `isLiveTrial`: status `trialing`, the door still open, and either
 * no deadline or a deadline that has not passed. A finished trial uses the
 * ordinary Free confirmation instead.
 */
function trialStillRunning(state: BillingState): boolean {
  if (state.status !== 'trialing' || state.locked) return false;
  if (!state.trialEndsAt) return true;
  const ends = Date.parse(state.trialEndsAt);
  return !Number.isFinite(ends) || ends > Date.now();
}
